import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { club, type ClubOverview } from '../../api/club'
import { ApiError } from '../../api/client'
import { AboutButton } from '@versports/ui'
import { AppNavBar } from '../../components/AppNavBar'
import { ImpersonationBanner } from '../../components/ImpersonationBanner'
import { ClubMark } from '../../components/ClubMark'
import { AddPlayerModal } from './AddPlayerModal'
import { InvitationsModal } from './InvitationsModal'
import { ClubReportDashboard } from './ClubReportDashboard'

type Tab = 'plantel' | 'actividad' | 'invitaciones' | 'informes'

export type OnboardingBadge = { label: string; className: string; title: string }

/**
 * Subset de flags consumido por `onboardingBadge` — lo exponen tanto
 * `ClubOverview.players[]` (tab Plantel/Actividad) como
 * `ClubPlayerTimeline.onboarding` (detalle de un jugador), para no duplicar
 * la lógica del semáforo en ningún lugar que muestre el estado.
 */
export type OnboardingFlags = {
  consent_ok: boolean
  profile_ok: boolean
  questionnaire_ok: boolean
  train_enabled: boolean
  sessions_count: number
}

/**
 * Semáforo de onboarding — deriva un badge del estado de un jugador. Orden
 * de prioridad y colores fijados por el producto (ver
 * backend `GET /club/{id}/overview` y `GET
 * /club/{id}/players/{userId}/timeline` — flags `consent_ok`/`profile_ok`/
 * `questionnaire_ok`/`train_enabled`/`sessions_count`, todos calculados server-side):
 *   1. sin consentimiento → gris/rojo suave
 *   2. perfil o cuestionario pendiente → amarillo
 *   3. todo ok pero VR no habilitado → azul
 *   4. VR habilitado, 0 sesiones → verde "listo para entrenar"
 *   5. >=1 sesión → verde "sesión N completada"
 */
export function onboardingBadge(p: OnboardingFlags): OnboardingBadge {
  if (!p.consent_ok) {
    return {
      label: 'Pendiente consentimiento',
      className: 'tag-err',
      title: 'Falta registrar el consentimiento informado del jugador.',
    }
  }
  if (!p.profile_ok || !p.questionnaire_ok) {
    const missing = [
      !p.profile_ok && 'perfil demográfico',
      !p.questionnaire_ok && 'cuestionario(s) pendiente(s)',
    ]
      .filter(Boolean)
      .join(' y ')
    return {
      label: 'Pendiente configuración',
      className: 'tag-warn',
      title: `Falta completar: ${missing}.`,
    }
  }
  if (!p.train_enabled) {
    return {
      label: 'Listo — habilitar casco',
      className: 'tag-info',
      title: 'Consentimiento, perfil y cuestionarios OK. Falta habilitar el acceso al VR ("Casco (Train)").',
    }
  }
  if (p.sessions_count === 0) {
    return {
      label: 'Listo para entrenar',
      className: 'tag-ok',
      title: 'VR habilitado, todavía sin sesiones cerradas.',
    }
  }
  return {
    label: `Sesión ${p.sessions_count} completada${p.sessions_count > 1 ? 's' : ''}`,
    className: 'tag-ok',
    title: `${p.sessions_count} sesión(es) cerrada(s).`,
  }
}

export function ClubPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [clubs, setClubs] = useState<Array<{ club_id: number; name: string; club_role: string }> | null>(null)
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
  const [overview, setOverview] = useState<ClubOverview | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('plantel')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddPlayer, setShowAddPlayer] = useState(false)
  const [showInvitations, setShowInvitations] = useState(false)
  const [trainTogglingId, setTrainTogglingId] = useState<number | null>(null)
  const [categorySavingId, setCategorySavingId] = useState<number | null>(null)
  const [copiedInvite, setCopiedInvite] = useState<string | null>(null)
  const [pinLinkLoadingId, setPinLinkLoadingId] = useState<number | null>(null)
  const [manualLink, setManualLink] = useState<{ url: string; label: string } | null>(null)

  /** Copia `url` al portapapeles. La Clipboard API falla fuera de HTTPS o sin
   *  gesto, y `window.prompt` está deshabilitado en webviews embebidas y en
   *  browsers donde el usuario tildó "bloquear diálogos": si los dos caminos
   *  fallan, el link se muestra igual en un campo seleccionable en vez de
   *  perderse detrás de un error. El manager nunca queda sin el link. */
  async function copyInviteLink(url: string, key: string, label = 'link de invitación') {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedInvite(key)
      setManualLink(null)
      setTimeout(() => setCopiedInvite((k) => (k === key ? null : k)), 2000)
      return
    } catch {
      // sigue al fallback
    }
    try {
      window.prompt(`Copiá el ${label}:`, url)
    } catch {
      setManualLink({ url, label })
    }
  }

  /** Copia el link `/set-pin` del jugador. El backend reusa el token vivo y
   *  emite uno nuevo sólo si venció, así que el botón se puede apretar sin
   *  miedo a invalidar el link que el jugador ya tenga. */
  async function handleCopyPinLink(userId: number) {
    if (!selectedClubId) return
    setPinLinkLoadingId(userId)
    try {
      const res = await club.playerPinLink(selectedClubId, userId)
      await copyInviteLink(res.link, `pin-${userId}`, 'link de PIN')
      setOverview((prev) =>
        prev
          ? {
              ...prev,
              players: prev.players.map((p) =>
                p.user_id === userId ? { ...p, pin_token_expires_at: res.expires_at } : p,
              ),
            }
          : prev,
      )
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'No pudimos generar el link de PIN. Reintentá en unos segundos.',
      )
    } finally {
      setPinLinkLoadingId(null)
    }
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await club.me()
        setClubs(data.clubs.map((c) => ({ club_id: c.club_id, name: c.name, club_role: c.club_role })))
        if (data.clubs.length > 0) {
          setSelectedClubId(data.clubs[0]!.club_id)
        }
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Error loading clubs')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [])

  useEffect(() => {
    if (!selectedClubId) return
    const loadOverview = async () => {
      setError(null)
      try {
        const data = await club.overview(selectedClubId)
        setOverview(data)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Error loading club')
      }
    }
    void loadOverview()
  }, [selectedClubId])

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <ImpersonationBanner />
        <main className="flex-1 pb-24 pt-16 flex items-center justify-center">
          <p className="text-text-muted">Cargando…</p>
        </main>
        <AboutButton navOffset />
        <AppNavBar />
      </div>
    )
  }

  if (!clubs || clubs.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <ImpersonationBanner />
        <main className="flex-1 pb-24 pt-16 flex items-center justify-center px-lg">
          <div className="card max-w-md text-center">
            <h2 className="text-lg font-semibold text-text-primary">No sos staff de ningún club</h2>
            <p className="mt-sm text-sm text-text-secondary">
              Para acceder al panel de club necesitás ser manager, técnico o admin de uno.
            </p>
            <a href="/" className="btn-primary mt-md inline-block no-underline">
              Volver al inicio
            </a>
          </div>
        </main>
        <AboutButton navOffset />
        <AppNavBar />
      </div>
    )
  }

  const primaryColor = overview?.club.primary_color ?? '#E25A1B'
  const secondaryColor = overview?.club.secondary_color
  const accentColor = overview?.club.accent_color

  async function handleToggleTrain(userId: number, nextEnabled: boolean) {
    if (!selectedClubId) return
    setTrainTogglingId(userId)
    setError(null)
    try {
      await club.setPlayerTrain(selectedClubId, userId, nextEnabled)
      const data = await club.overview(selectedClubId)
      setOverview(data)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al actualizar el acceso al VR.')
    } finally {
      setTrainTogglingId(null)
    }
  }

  async function handleCategorySave(userId: number, nextCategory: string) {
    if (!selectedClubId) return
    setCategorySavingId(userId)
    setError(null)
    try {
      await club.updatePlayer(selectedClubId, userId, { category: nextCategory })
      const data = await club.overview(selectedClubId)
      setOverview(data)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al actualizar la categoría.')
    } finally {
      setCategorySavingId(null)
    }
  }

  const totalSessions = (overview?.players ?? []).reduce((sum, p) => sum + p.n_sessions, 0)
  const respondedQuestionnaires = (overview?.players ?? []).reduce(
    (sum, p) => sum + p.questionnaires.length,
    0,
  )

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        ['--club-primary' as string]: primaryColor,
        ['--club-secondary' as string]: secondaryColor ?? primaryColor,
        ['--club-accent' as string]: accentColor ?? primaryColor,
      }}
    >
      <ImpersonationBanner />

      {/* Header */}
      <header className="border-t-4 border-[var(--club-primary)] bg-surface shadow-[0_12px_36px_rgba(226,90,27,0.12)]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-lg py-md">
          <div className="flex items-center gap-md">
            {overview && (
              <ClubMark
                logoUrl={overview.club.logo_url}
                name={overview.club.name}
                size={40}
              />
            )}
            <div>
              <h1 className="m-0 text-xl font-bold text-text-primary">
                {overview?.club.name}
                {overview?.club.division && (
                  <span className="ml-sm text-sm font-normal text-text-muted">
                    · {overview.club.division}
                  </span>
                )}
              </h1>
              <span className="tag text-xs text-[var(--club-primary)]">{overview?.club.club_role}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="btn-ghost px-md py-xs text-xs"
          >
            Salir
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pb-24">
        <div className="mx-auto max-w-6xl px-lg py-lg">
          {error && <div className="banner-err mb-lg">{error}</div>}

          {manualLink && (
            <div className="banner-ok mb-lg">
              <p className="m-0 text-sm">
                No pudimos copiarlo al portapapeles. Copiá el {manualLink.label} a mano:
              </p>
              <input
                className="input mt-xs w-full font-mono text-xs"
                readOnly
                value={manualLink.url}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="btn mt-xs"
                onClick={() => setManualLink(null)}
              >
                Cerrar
              </button>
            </div>
          )}

          {/* Club selector if more than one */}
          {clubs.length > 1 && (
            <div className="mb-lg">
              <label htmlFor="club-select" className="block text-sm font-medium text-text-muted mb-xs">
                Seleccionar club
              </label>
              <select
                id="club-select"
                value={selectedClubId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value)
                  setSelectedClubId(Number.isFinite(id) ? id : null)
                }}
                className="input"
              >
                {clubs.map((c) => (
                  <option key={c.club_id} value={c.club_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!overview ? (
            <p className="text-sm text-text-muted">Cargando datos del club…</p>
          ) : (
            <>
              {/* Servicio vencido — banner bloqueante/prominente, siempre visible
                  arriba de las tabs. No oculta datos: el manager sigue viendo su
                  plantel/exportando, solo se le avisa fuerte (decisión de alcance
                  MVP — ver app/services/auth.py::can_train, que SÍ bloquea la
                  entrada al VR de los jugadores). */}
              {!overview.club.service_active && (
                <div className="banner-err mb-lg text-sm font-medium">
                  Servicio vencido
                  {overview.club.service_until
                    ? ` desde el ${new Date(`${overview.club.service_until}T00:00:00`).toLocaleDateString('es-AR')}`
                    : ''}
                  {' — '}contactá a Humai para renovar. Los jugadores de este club
                  no pueden entrar al VR hasta que se renueve el servicio.
                </div>
              )}

              {/* Stats cards */}
              <div className="mb-lg grid grid-cols-3 gap-md">
                <div className="stat-card">
                  <div className="stat-card-label">Jugadores</div>
                  <div className="stat-card-value text-[var(--club-primary)]">
                    {overview.club.player_count} / {overview.club.seat_limit ?? '∞'}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-label">Sesiones totales</div>
                  <div className="stat-card-value text-[var(--club-primary)]">{totalSessions}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-label">Cuestionarios respondidos</div>
                  <div className="stat-card-value text-[var(--club-primary)]">
                    {respondedQuestionnaires}
                  </div>
                </div>
              </div>

              {/* Tab strip */}
              <div className="mb-lg flex flex-wrap gap-1 border-b border-outline">
                <button type="button" onClick={() => setActiveTab('informes')} className={`px-md py-sm text-sm font-medium ${activeTab === 'informes' ? 'border-b-2 border-primary text-primary' : 'text-text-secondary'}`}>Informes</button>
                <button
                  type="button"
                  onClick={() => setActiveTab('plantel')}
                  className={`px-md py-sm text-sm font-medium transition-colors ${
                    activeTab === 'plantel'
                      ? 'border-b-2 border-[var(--club-primary)] text-[var(--club-primary)]'
                      : 'border-b-2 border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Plantel
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('actividad')}
                  className={`px-md py-sm text-sm font-medium transition-colors ${
                    activeTab === 'actividad'
                      ? 'border-b-2 border-[var(--club-primary)] text-[var(--club-primary)]'
                      : 'border-b-2 border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Actividad
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('invitaciones')}
                  className={`px-md py-sm text-sm font-medium transition-colors ${
                    activeTab === 'invitaciones'
                      ? 'border-b-2 border-[var(--club-primary)] text-[var(--club-primary)]'
                      : 'border-b-2 border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Invitaciones
                </button>
              </div>

              {/* Plantel tab */}
              {activeTab === 'informes' && selectedClubId && <ClubReportDashboard key={selectedClubId} clubId={selectedClubId} />}
              {activeTab === 'plantel' && (
                <div>
                  <div className="mb-md flex gap-md">
                    <button
                      type="button"
                      onClick={() => setShowAddPlayer(true)}
                      className="btn-primary"
                    >
                      + Agregar jugador
                    </button>
                    <a
                      href={club.exportUrl(overview.club.club_id)}
                      download
                      className="btn no-underline"
                    >
                      Exportar CSV
                    </a>
                  </div>
                  <div className="table-wrap border-t-4 border-t-[var(--club-primary)]">
                    <table>
                      <thead>
                        <tr>
                          <th className="text-text-primary">Nombre</th>
                          <th className="text-text-primary">Email</th>
                          <th className="text-text-primary">Alta</th>
                          <th className="text-text-primary">Categoría</th>
                          <th className="text-text-primary">Estado</th>
                          <th className="text-text-primary">Onboarding</th>
                          <th className="text-text-primary">PIN</th>
                          <th className="text-text-primary">Casco (Train)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.players.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center text-text-muted">
                              Sin jugadores en este club
                            </td>
                          </tr>
                        ) : (
                          overview.players.map((p) => {
                            const badge = onboardingBadge(p)
                            return (
                            <tr key={p.user_id}>
                              <td className="font-medium text-text-primary">{p.display_name}</td>
                              <td>{p.email || '—'}</td>
                              <td className="text-sm text-text-secondary">
                                {p.date_joined
                                  ? new Date(p.date_joined).toLocaleDateString('es-AR')
                                  : '—'}
                              </td>
                              <td>
                                <input
                                  type="text"
                                  defaultValue={p.category ?? ''}
                                  placeholder="—"
                                  maxLength={64}
                                  disabled={categorySavingId === p.user_id}
                                  className="input max-w-[140px] py-xs text-sm"
                                  onBlur={(e) => {
                                    const next = e.target.value.trim()
                                    if (next !== (p.category ?? '')) void handleCategorySave(p.user_id, next)
                                  }}
                                />
                              </td>
                              <td>
                                <span
                                  className={`tag ${p.is_active ? 'tag-ok' : ''}`}
                                  style={!p.is_active ? { opacity: 0.6 } : {}}
                                >
                                  {p.is_active ? 'activo' : 'inactivo'}
                                </span>
                              </td>
                              <td>
                                <span className={badge.className} title={badge.title}>
                                  {badge.label}
                                </span>
                              </td>
                              <td className="text-sm">
                                {p.has_pin ? (
                                  <span className="tag tag-ok" title="El jugador ya fijó su PIN">
                                    configurado
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={pinLinkLoadingId === p.user_id}
                                    onClick={() => void handleCopyPinLink(p.user_id)}
                                    className="btn-ghost text-xs no-underline disabled:opacity-50"
                                    title={
                                      p.pin_token_expires_at
                                        ? `Copiar link de PIN (vigente hasta ${new Date(
                                            p.pin_token_expires_at,
                                          ).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })})`
                                        : 'Copiar link de PIN (para mandarlo por WhatsApp)'
                                    }
                                  >
                                    {copiedInvite === `pin-${p.user_id}`
                                      ? '✓ copiado'
                                      : pinLinkLoadingId === p.user_id
                                        ? '…'
                                        : 'Copiar link de PIN ⧉'}
                                  </button>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={p.train_enabled}
                                  disabled={trainTogglingId === p.user_id}
                                  onClick={() => void handleToggleTrain(p.user_id, !p.train_enabled)}
                                  className={`inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                                    p.train_enabled ? 'bg-success' : 'bg-outline'
                                  }`}
                                  title={p.train_enabled ? 'Deshabilitar Train' : 'Habilitar Train'}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      p.train_enabled ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </td>
                            </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actividad tab */}
              {activeTab === 'actividad' && (
                <div className="table-wrap border-t-4 border-t-[var(--club-primary)]">
                  <table>
                    <thead>
                      <tr>
                        <th className="text-text-primary">Nombre</th>
                        <th className="text-text-primary">Sesiones</th>
                        <th className="text-text-primary">Última sesión</th>
                        <th className="text-text-primary">Cuestionarios</th>
                        <th className="text-text-primary">Invitaciones</th>
                        <th className="text-text-primary">Onboarding</th>
                        <th className="text-right text-text-primary">Ver resultados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.players.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-text-muted">
                            Sin jugadores en este club
                          </td>
                        </tr>
                      ) : (
                        overview.players.map((p) => {
                          const badge = onboardingBadge(p)
                          return (
                          <tr key={p.user_id}>
                            <td className="font-medium text-text-primary">{p.display_name}</td>
                            <td className="text-sm text-text-secondary">{p.n_sessions}</td>
                            <td className="text-sm text-text-secondary">
                              {p.last_session_at
                                ? new Date(p.last_session_at).toLocaleDateString('es-AR', {
                                    dateStyle: 'short',
                                  })
                                : '—'}
                            </td>
                            <td className="text-sm">
                              {p.questionnaires.length > 0 ? (
                                <div className="flex flex-wrap gap-xs">
                                  {p.questionnaires.map((q) => (
                                    <span key={`${q.code}-${q.timepoint}`} className="tag text-xs">
                                      {q.code}·{q.timepoint}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-text-muted">—</span>
                              )}
                            </td>
                            <td className="text-sm">
                              {p.pending_invitations.length > 0 ? (
                                <div className="flex flex-wrap gap-xs">
                                  {p.pending_invitations.map((inv) => (
                                    <button
                                      key={inv.code}
                                      type="button"
                                      className="tag text-xs cursor-pointer"
                                      title="Copiar link de invitación (para reenviar por WhatsApp)"
                                      onClick={() => void copyInviteLink(inv.link_url, `${p.user_id}-${inv.code}`)}
                                    >
                                      {copiedInvite === `${p.user_id}-${inv.code}` ? '✓ copiado' : `${inv.code} ⧉`}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-text-muted">—</span>
                              )}
                            </td>
                            <td className="text-sm">
                              <span className={badge.className} title={badge.title}>
                                {badge.label}
                              </span>
                            </td>
                            <td className="text-right">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/club/players/${p.user_id}?clubId=${overview.club.club_id}`,
                                  )
                                }
                                className="btn-ghost text-xs no-underline"
                              >
                                detalle
                              </button>
                            </td>
                          </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Invitaciones tab */}
              {activeTab === 'invitaciones' && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowInvitations(true)}
                    className="btn-primary mb-md"
                  >
                    Enviar invitaciones
                  </button>
                </div>
              )}

              {/* Modals */}
              {showAddPlayer && selectedClubId && (
                <AddPlayerModal
                  clubId={selectedClubId}
                  seatLimit={overview.club.seat_limit}
                  playerCount={overview.club.player_count}
                  onClose={() => {
                    setShowAddPlayer(false)
                    void (async () => {
                      const data = await club.overview(selectedClubId)
                      setOverview(data)
                    })()
                  }}
                />
              )}
              {showInvitations && selectedClubId && (
                <InvitationsModal
                  clubId={selectedClubId}
                  onClose={() => {
                    setShowInvitations(false)
                    void (async () => {
                      const data = await club.overview(selectedClubId)
                      setOverview(data)
                    })()
                  }}
                />
              )}
            </>
          )}
        </div>
      </main>

      <AboutButton navOffset />
      <AppNavBar />
    </div>
  )
}
